import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

/**
 * Cabeçalho da PÁGINA: título, o número que importa e as ações.
 *
 * Trilha e busca moram na barra de topo, que é do shell e é igual em toda
 * tela. Aqui fica só o que é desta página. Em tela de detalhe, `back` põe o
 * "← Voltar" acima do título — é o caminho que o operador quer, e não uma
 * trilha inteira repetindo o menu.
 *
 * Ícone ao lado do título não existe, e é de propósito. Cada uma das 50 telas
 * tinha uma frase narrando a si mesma e metade um ícone num quadradinho
 * colorido ao lado do título. Regra de negócio que o operador precisa saber
 * vai no campo ou na coluna onde ela vale, não num parágrafo no topo.
 *
 * `description` existe para a linha que SITUA a tela — a data de hoje na
 * Visão Geral — e não para narrar o que a tela faz.
 *
 * NÚMERO é outra coisa, e tem lugar próprio: `meta`. Rótulo apagado, valor em
 * destaque, sem frase em volta.
 */
export interface HeaderMeta {
  label: string;
  value: ReactNode;
  /** Só quando o número pede ação: vencido, estourado. */
  tone?: 'bad' | 'warn';
}

export function WorkspaceHeader({
  title,
  meta,
  description,
  actions,
  back,
}: {
  title: ReactNode;
  /** Uma linha curta sob o título. Só quando situa a tela; nunca para narrar. */
  description?: ReactNode;
  meta?: HeaderMeta[];
  actions?: ReactNode;
  /** Tela de detalhe: para onde o "Voltar" leva. */
  back?: { to: string; label?: string };
}) {
  return (
    <header className="pagehead">
      <div className="pagehead__main">
        {back && (
          <Link to={back.to} className="pagehead__back">
            <ArrowLeft size={14} aria-hidden />
            {back.label ?? 'Voltar'}
          </Link>
        )}
        <h1 className="pagehead__title">{title}</h1>
        {description && <p className="pagehead__desc">{description}</p>}
        {meta && meta.length > 0 && (
          <dl className="pagehead__meta">
            {meta.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd className={item.tone ? `is-${item.tone}` : undefined}>{item.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {actions && <div className="pagehead__actions">{actions}</div>}
    </header>
  );
}
